import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { PaPageHeader } from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import { getTimeOfDayGreeting } from "pages/PlatformAdmin/platformAdminPage.utils";
import { getUser } from "store/selector/auth.selector";
import { usePlatformDashboardCapabilities } from "pages/PlatformAdmin/hooks/usePlatformPermissions";
import {
  buildDashboardQuickActions,
  getPlatformRoleLabel,
  hasAnyPlatformAdminAccess,
} from "./platformDashboardCapabilities.utils";
import { getGreetingName } from "./platformDashboard.utils";

const PlatformAdminWorkspace = () => {
  const currentUser = useSelector(getUser);
  const capabilities = usePlatformDashboardCapabilities();
  const quickActions = useMemo(
    () => buildDashboardQuickActions(capabilities),
    [capabilities]
  );

  if (!hasAnyPlatformAdminAccess()) {
    return (
      <PaAccessDenied
        title="Platform admin"
        subtitle="No assigned permissions"
        description="Contact a Platform Owner to assign a platform role."
      />
    );
  }

  const roleLabel = getPlatformRoleLabel(currentUser);

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title={`${getTimeOfDayGreeting()}, ${getGreetingName(currentUser)}`}
        subtitle={`${roleLabel} · Choose an area below`}
        className="mb-4"
      />

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Your workspace</h2>
        <p className="mt-1 text-sm text-gray-500">
          Tools available for your platform role. The full dashboard is shown when you have
          platform dashboard access.
        </p>

        <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {quickActions.map((action) => (
            <li key={action.id}>
              <Link
                to={action.to}
                className="block h-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 transition hover:border-indigo-200 hover:bg-indigo-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <p className="text-sm font-semibold text-gray-900">{action.label}</p>
                <p className="mt-1 text-xs text-gray-600">{action.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </PlatformAdminLayout>
  );
};

export default PlatformAdminWorkspace;
